import React, { FC } from 'react';
import { VideoIframeProps, VideoOptimizerProps } from './type';
import { PrismicImageWithAlt } from '../PrismicImageWithAlt';
import classNames from 'classnames';

const getYouTubeVideoId = (url: string): string | null => {
  try {
    const parsed = new URL(url);

    if (parsed.hostname.includes('youtu.be')) {
      return parsed.pathname.replace(/^\//, '').split('/')[0] || null;
    }

    if (parsed.hostname.includes('youtube.com')) {
      if (parsed.pathname.startsWith('/embed/')) {
        return parsed.pathname.split('/')[2] || null;
      }

      return parsed.searchParams.get('v');
    }
  } catch {
    return null;
  }

  return null;
};

const buildYouTubeEmbedSrc = (
  videoUrl: string,
  {
    autoplay = false,
    controls = 'Hide',
    loop = true,
  }: Pick<VideoIframeProps, 'autoplay' | 'controls' | 'loop'>,
) => {
  const videoId = getYouTubeVideoId(videoUrl);
  if (!videoId) {
    return videoUrl;
  }

  const params = new URLSearchParams({
    playsinline: '1',
    rel: '0',
    modestbranding: '1',
  });

  if (controls === 'Detailed') {
    params.set('controls', '1');
  } else {
    params.set('controls', '0');
    params.set('iv_load_policy', '3');
  }

  if (autoplay) {
    params.set('autoplay', '1');
    params.set('mute', '1');
  }

  if (controls === 'Hide' && autoplay) {
    params.set('disablekb', '1');
  }

  if (loop) {
    params.set('loop', '1');
    params.set('playlist', videoId);
  }

  return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
};

export const VideoOptimizer: FC<VideoOptimizerProps> = ({
  videoRef,
  videoUrl,
  poster,
  alt = 'Video content',
  autoplay = false,
  loop = true,
  muted = true,
  controls,
  playsInline = true,
  maxHeight,
  aspectRatio,
  objectFit = 'cover',
  contrast = 100,
  brightness = 100,
  onPlay,
  onPause,
  onCanPlay,
  onLoadedData,
  preload = 'none',
  imageClass,
  videoClass,
  ariaLabel,
  enableKeyboardControls = true,
  isDecorative = false,
  onPlayPause,
}) => {
  const isInteractive = !isDecorative && enableKeyboardControls;

  const handleKeyDown = (event: React.KeyboardEvent<HTMLVideoElement>) => {
    if (!isInteractive) return;

    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      onPlayPause?.();
    }
  };

  // If there's no video, show poster
  if (!videoUrl) {
    return (
      <PrismicImageWithAlt
        className={imageClass}
        defaultAlt={alt}
        field={poster}
        priority
        showPlaceholderIfEmpty
        sizes="100vw"
        style={{
          objectFit: maxHeight ? 'cover' : 'contain',
          objectPosition: 'center',
          aspectRatio: !maxHeight ? aspectRatio : undefined,
          width: '100%',
          maxHeight,
        }}
      />
    );
  }

  return (
    <video
      aria-hidden={isDecorative ? true : undefined}
      aria-label={isDecorative ? undefined : ariaLabel || alt}
      autoPlay={autoplay}
      className={classNames(videoClass)}
      controls={controls === 'Detailed'}
      loop={loop}
      muted={muted}
      onCanPlay={onCanPlay}
      onKeyDown={handleKeyDown}
      onLoadedData={onLoadedData}
      onPause={onPause}
      onPlay={onPlay}
      playsInline={playsInline}
      {...(poster?.url && { poster: poster.url })}
      preload={preload}
      ref={videoRef}
      style={{
        aspectRatio,
        filter: `contrast(${contrast}%) brightness(${brightness}%)`,
        maxHeight,
        objectFit,
      }}
      tabIndex={isInteractive ? 0 : -1}
    >
      <source src={videoUrl} />
    </video>
  );
};

export const VideoIframe: FC<VideoIframeProps> = ({
  videoUrl,
  poster,
  alt = 'Video content',
  autoplay = false,
  controls = 'Hide',
  loop = true,
  maxHeight,
  aspectRatio,
  title = 'Video content',
  className,
  imageClass,
  style,
}) => {
  const ytVideoSrc = buildYouTubeEmbedSrc(videoUrl, {
    autoplay,
    controls,
    loop,
  });
  const isBackgroundVideo = controls === 'Hide' && autoplay;

  // If there's no video, show poster
  if (!videoUrl) {
    return (
      <PrismicImageWithAlt
        className={imageClass}
        defaultAlt={alt}
        field={poster}
        priority
        showPlaceholderIfEmpty
        sizes="100vw"
        style={{
          objectFit: maxHeight ? 'cover' : 'contain',
          objectPosition: 'center',
          aspectRatio: !maxHeight ? aspectRatio : undefined,
          width: '100%',
          maxHeight,
        }}
      />
    );
  }

  return (
    <iframe
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen={controls === 'Detailed'}
      aria-hidden={isBackgroundVideo ? true : undefined}
      aria-label={isBackgroundVideo ? undefined : title}
      className={className}
      src={ytVideoSrc}
      style={{
        aspectRatio,
        maxHeight,
        ...style,
      }}
      title={title}
    />
  );
};
